import * as Yup from 'yup';

export default Yup;

// Common nullable number validation (converts empty string to null)
const nullableNumber = Yup.number()
  .nullable()
  .transform((value, originalValue) =>
    originalValue === '' || Number.isNaN(originalValue) ? null : value
  );

// Common validations used across entities
export const validations = {
  // Nullable number that must be >= 0
  nullablePositiveNumber: nullableNumber.min(0, 'Must be a positive number'),

  // Required positive number
  requiredPositiveNumber: Yup.number()
    .required('Required')
    .min(0, 'Must be a positive number'),

  // Budget validation (in cents for FB API)
  budget: nullableNumber
    .min(1, 'Budget must be at least $1')
    .max(10000000, 'Budget cannot exceed $10,000,000'),

  // Percentage validation (0-100)
  percentage: nullableNumber
    .min(0, 'Must be at least 0%')
    .max(100, 'Cannot exceed 100%'),

  // Required string with trim
  requiredString: Yup.string()
    .trim()
    .required('Required'),

  // Optional string with trim
  optionalString: Yup.string()
    .trim()
    .nullable(),

  // URL validation
  url: Yup.string()
    .trim()
    .url('Must be a valid URL')
    .nullable(),

  // UUID validation
  uuid: Yup.string()
    .uuid('Must be a valid ID')
    .required('Required'),
};

// Facebook-specific validations
export const fbValidations = {
  // Campaign name (1-400 characters)
  campaignName: Yup.string()
    .trim()
    .required('Campaign name is required')
    .min(1, 'Name must be at least 1 character')
    .max(400, 'Name cannot exceed 400 characters'),

  // Ad set name
  adSetName: Yup.string()
    .trim()
    .required('Ad set name is required')
    .min(1, 'Name must be at least 1 character')
    .max(400, 'Name cannot exceed 400 characters'),

  // Ad name
  adName: Yup.string()
    .trim()
    .required('Ad name is required')
    .min(1, 'Name must be at least 1 character')
    .max(400, 'Name cannot exceed 400 characters'),

  // Facebook campaign objectives
  objective: Yup.string()
    .required('Objective is required')
    .oneOf(
      ['AWARENESS', 'TRAFFIC', 'ENGAGEMENT', 'LEADS', 'APP_PROMOTION', 'SALES'],
      'Invalid objective'
    ),

  // Facebook entity status
  status: Yup.string()
    .required('Status is required')
    .oneOf(['ACTIVE', 'PAUSED', 'DELETED', 'ARCHIVED'], 'Invalid status'),

  // Bid strategy
  bidStrategy: Yup.string()
    .nullable()
    .oneOf(
      [null, 'LOWEST_COST_WITHOUT_CAP', 'LOWEST_COST_WITH_BID_CAP', 'COST_CAP', 'MINIMUM_ROAS'],
      'Invalid bid strategy'
    ),

  // Optimization goal
  optimizationGoal: Yup.string()
    .nullable()
    .oneOf(
      [
        null, 'NONE', 'APP_INSTALLS', 'AD_RECALL_LIFT', 'ENGAGED_USERS',
        'EVENT_RESPONSES', 'IMPRESSIONS', 'LEAD_GENERATION', 'QUALITY_LEAD',
        'LINK_CLICKS', 'OFFSITE_CONVERSIONS', 'PAGE_LIKES', 'POST_ENGAGEMENT',
        'QUALITY_CALL', 'REACH', 'LANDING_PAGE_VIEWS', 'VISIT_INSTAGRAM_PROFILE',
        'VALUE', 'THRUPLAY', 'DERIVED_EVENTS', 'APP_INSTALLS_AND_OFFSITE_CONVERSIONS',
        'CONVERSATIONS', 'IN_APP_VALUE', 'MESSAGING_PURCHASE_CONVERSION',
        'MESSAGING_APPOINTMENT_CONVERSION', 'SUBSCRIBERS', 'REMINDERS_SET',
      ],
      'Invalid optimization goal'
    ),

  // Billing event
  billingEvent: Yup.string()
    .nullable()
    .oneOf(
      [null, 'APP_INSTALLS', 'CLICKS', 'IMPRESSIONS', 'LINK_CLICKS',
       'LISTING_INTERACTION', 'NONE', 'OFFER_CLAIMS', 'PAGE_LIKES',
       'POST_ENGAGEMENT', 'PURCHASE', 'THRUPLAY'],
      'Invalid billing event'
    ),
};

// Validation helper for conditional budget validation
export const budgetValidation = {
  // Either daily or lifetime, not both
  dailyOrLifetime: Yup.object().test(
    'daily-or-lifetime',
    'Set either daily budget or lifetime budget, not both',
    function (value) {
      const { daily_budget, lifetime_budget } = value as Record<string, unknown>;
      if (daily_budget && lifetime_budget) {
        return false;
      }
      return true;
    }
  ),
};
